/**
 * API-laag voor de werkplek (v4).
 *
 * Bewust een eigen bestand naast `lib/api.ts`: dat bestand is 2.500+
 * regels en wordt door elk bestaand scherm gebruikt. De vier bouwers
 * werken tegelijk; een gedeelde bewerking daar zou bij elke merge
 * botsen. Alles wat de werkplek nodig heeft staat hier.
 *
 * Elke bouwer voegt zijn eigen `export const <naam>Api = { … }` toe
 * onderaan; niemand raakt de blokken van een ander aan.
 */
import { apiClient } from "@/lib/api";

// ── Types uit app/schemas/werkplek.py ────────────────────────────────

export interface WerkplekStand {
  werkplek_v4: boolean;
  mag_beheren: boolean;
}

export interface WerkplekInstellingen {
  waarden: Record<string, unknown>;
}

export interface ActieSoort {
  soort: string;
  groep: string;
  label: string;
  /** "werkend" | "droog" | "nog_niet_aangesloten" */
  stand: string;
  reden_uit: string | null;
  raakt_geld: boolean;
  terugdraaibaar: boolean;
}

export interface WerkplekActie {
  id: string;
  soort: string;
  status: string;
  zaak_id: string | null;
  klant_id: string | null;
  parameters: Record<string, unknown>;
  bevestigtekst: string | null;
  bedrag_cents: number | null;
  droog: boolean;
  resultaat: Record<string, unknown> | null;
  foutmelding: string | null;
  bevestigd_op: string | null;
  uitgevoerd_op: string | null;
  ongedaan_tot: string | null;
  ongedaan_op: string | null;
}

export interface ZaakOnderdeel {
  id: string;
  soort: string;
  bron_id: string;
  losgemaakt_op: string | null;
}

export interface ZaakWekker {
  id: string;
  reden: string;
  status: string;
  afloopt_op: string;
  termijn_werkdagen: number | null;
  follow_up_tekst: string | null;
}

export interface Zaak {
  id: string;
  status: string;
  klant_id: string | null;
  onderwerp: string | null;
  kanaal_hoofd: string | null;
  laatste_activiteit_op: string;
  afgerond_op: string | null;
  samengevoegd_in_zaak_id: string | null;
  onderdelen: ZaakOnderdeel[];
  wekkers: ZaakWekker[];
}

export interface TijdlijnRegel {
  id: string;
  moment: string;
  soort: string;
  tekst: string;
  door_soort: string;
  actie_id: string | null;
  terugdraaibaar: boolean;
  teruggedraaid_op: string | null;
  details: Record<string, unknown> | null;
}

// ── Fundament ────────────────────────────────────────────────────────

export interface WerkplekMenutellers {
  vandaag: number;
  retouren: number;
  reviews: number;
  /** Open technische signalen (sessie 47, 3e). */
  signalen?: number;
}

export const werkplekApi = {
  /** Staat de werkplek aan, en mag deze gebruiker hem beheren? */
  async stand(): Promise<WerkplekStand> {
    const { data } = await apiClient.get<WerkplekStand>("/api/werkplek/stand");
    return data;
  },

  /** De badges op het menu; één verzoek voor alle drie (punt 13). */
  async menutellers(): Promise<WerkplekMenutellers> {
    const { data } = await apiClient.get<WerkplekMenutellers>(
      "/api/werkplek/menutellers",
    );
    return data;
  },

  async instellingen(): Promise<WerkplekInstellingen> {
    const { data } = await apiClient.get<WerkplekInstellingen>(
      "/api/werkplek/instellingen",
    );
    return data;
  },
  /** Sessie 72: alleen de schermsleutels, voor iedere ingelogde medewerker. */
  async schermInstellingen(): Promise<WerkplekInstellingen> {
    const { data } = await apiClient.get<WerkplekInstellingen>(
      "/api/werkplek/instellingen/scherm",
    );
    return data;
  },

  async zetInstelling(
    sleutel: string,
    waarde: unknown,
  ): Promise<WerkplekInstellingen> {
    const { data } = await apiClient.put<WerkplekInstellingen>(
      "/api/werkplek/instellingen",
      { sleutel, waarde },
    );
    return data;
  },

  async actiesoorten(): Promise<ActieSoort[]> {
    const { data } = await apiClient.get<ActieSoort[]>(
      "/api/werkplek/acties/soorten",
    );
    return data;
  },

  /** Legt een actie klaar. Er gebeurt nog niets naar buiten. */
  async stelActieVoor(invoer: {
    soort: string;
    zaak_id?: string | null;
    klant_id?: string | null;
    parameters?: Record<string, unknown>;
    bevestigtekst?: string | null;
    bedrag_cents?: number | null;
  }): Promise<WerkplekActie> {
    const { data } = await apiClient.post<WerkplekActie>(
      "/api/werkplek/acties",
      invoer,
    );
    return data;
  },

  async bevestigActie(actieId: string): Promise<WerkplekActie> {
    const { data } = await apiClient.post<WerkplekActie>(
      `/api/werkplek/acties/${actieId}/bevestig`,
    );
    return data;
  },

  async maakActieOngedaan(
    actieId: string,
    negeerVenster = false,
  ): Promise<WerkplekActie> {
    const { data } = await apiClient.post<WerkplekActie>(
      `/api/werkplek/acties/${actieId}/ongedaan`,
      null,
      { params: { negeer_venster: negeerVenster } },
    );
    return data;
  },

  async annuleerActie(actieId: string): Promise<WerkplekActie> {
    const { data } = await apiClient.post<WerkplekActie>(
      `/api/werkplek/acties/${actieId}/annuleer`,
    );
    return data;
  },

  async zaak(zaakId: string): Promise<Zaak> {
    const { data } = await apiClient.get<Zaak>(
      `/api/werkplek/zaken/${zaakId}`,
    );
    return data;
  },

  async tijdlijn(zaakId: string): Promise<TijdlijnRegel[]> {
    const { data } = await apiClient.get<TijdlijnRegel[]>(
      `/api/werkplek/zaken/${zaakId}/tijdlijn`,
    );
    return data;
  },
};

// ── Klantpagina (bouwer 4) ───────────────────────────────────────────
// Spiegelt app/schemas/werkplek_klanten.py. Geld reist als centen
// (int); momenten zijn ISO-8601 in UTC en worden hier pas naar
// Europe/Amsterdam omgezet.

export interface WerkplekKlantRegel {
  id: string;
  naam: string;
  woonplaats: string | null;
  aantal_orders: number;
  laatste_contact_op: string | null;
  klant_sinds: string | null;
  open_zaken: number;
  /** Is dat "contact" eigenlijk de laatste order? (JP, punt 27.) */
  laatste_contact_is_order?: boolean;
  dieren: string[];
}

export interface WerkplekKlantenLijst {
  klanten: WerkplekKlantRegel[];
  /** Telt de lijst hierboven; absoluut te toetsen. */
  teller: number;
}

export interface WerkplekVoedingProduct {
  id: string;
  naam: string;
  /** "Eerder gekocht" als de klant het al bestelde. */
  herkomst: string | null;
  /** Kan de rekenlaag dit product doorrekenen? */
  rekenbaar: boolean;
}

export interface WerkplekDier {
  id: string;
  naam: string;
  diersoort: string;
  ras: string | null;
  leeftijd_maanden: number | null;
  gewicht_kg: number | null;
  castratie: boolean | null;
  activiteitsniveau: string | null;
  conditiescore: number | null;
  streefgewicht_kg: number | null;
  verwacht_volwassen_gewicht_kg: number | null;
  /** Velden die leeg zijn: hier hoort "onbekend, vraag na". */
  onbekend: string[];
  /** Velden die op een aanname berusten. */
  aannames: string[];
}

export interface WerkplekKlantPagina {
  id: string;
  naam: string;
  /** Telt de klantrij iets anders dan het portaal? (JP, punt 25.) */
  orders_verschil?: string | null;
  /**
   * Sessie 72 (punt 8): hoeveel orders `/orders` zonder limiet geeft,
   * ontdubbeld (zelfde verzameling als de lijst). De teller van het
   * klantblok in werkplek v6.
   */
  orders_in_lijst?: number;
  voornaam: string | null;
  achternaam: string | null;
  bedrijfsnaam: string | null;
  email: string | null;
  telefoon: string | null;
  mobiel: string | null;
  adres: string | null;
  klant_sinds: string | null;
  /** Waar klant_sinds vandaan komt: "klantrij" of "eerste order". */
  klant_sinds_bron: string | null;
  klant_type: string;
  lightspeed_id: string | null;
  lightspeed_url: string | null;
  bel_url: string | null;
  whatsapp_url: string | null;
  aantal_orders: number;
  laatste_order_op: string | null;
  lifetime_value_cents: number;
  gemiddelde_orderwaarde_cents: number;
  aantal_retouren: number;
  open_zaken: number;
  laatst_gesynct_op: string | null;
  sync_bron: string | null;
  dieren: WerkplekDier[];
  samengevoegde_accounts: Record<string, unknown>[];
  samenvoegen_beschikbaar: boolean;
  samenvoegen_reden_uit: string | null;
  notities: string | null;
}

export interface WerkplekKlantOrder {
  id: string;
  lightspeed_id: string | null;
  lightspeed_url: string | null;
  ordernummer: string;
  besteld_op: string | null;
  bedrag_cents: number;
  status: string;
  /** De status in het Nederlands; de backend vertaalt (punt 10). */
  status_label: string;
  betaal_status: string | null;
}

export interface WerkplekKlantGesprek {
  kanaal: string;
  bron_id: string;
  moment: string;
  titel: string;
  voorproef: string | null;
  doorklik_id: string | null;
}

export interface WerkplekKlantNotitie {
  sleutel: string;
  moment: string | null;
  tekst: string;
  door_soort: string;
  zaak_id: string | null;
}

export interface WerkplekVoedingUitkomst {
  foutcode: string | null;
  reden: string | null;
  gram_per_dag: number | null;
  gram_band: [number, number] | null;
  kcal_per_dag: number | null;
  dagen_per_zak: number | null;
  volgende_zak_op: string | null;
  aannames: string[];
  energie_bron: string | null;
  product_naam: string | null;
  dier_naam: string | null;
  antwoordtekst: string | null;
  /** Sessie 188: startgetal om af te vallen, op het streefgewicht. */
  afvallen?: boolean;
  diersoort?: string | null;
  streefgewicht_kg?: number | null;
  eiwit_code?: string | null;
  eiwit_gram_per_dag?: number | null;
  eiwit_nodig_gram_per_dag?: number | null;
}

export interface WerkplekToegangRegel {
  id: string;
  naam: string;
  email: string;
  /** De databaserol zoals hij is: alleen_lezen / medewerker / admin / toets. */
  rol: string;
  /** Hetzelfde recht in v4-taal: meekijken / beantwoorden / beheer / Toets. */
  toegang: string;
  toegang_label: string;
  actief: boolean;
  twee_factor_actief: boolean;
  laatst_ingelogd_op: string | null;
}

export interface WerkplekToegangLijst {
  gebruikers: WerkplekToegangRegel[];
  teller: number;
  mag_beheren: boolean;
  reden_uit: string | null;
}

export const werkplekKlantenApi = {
  async lijst(zoek?: string): Promise<WerkplekKlantenLijst> {
    const { data } = await apiClient.get<WerkplekKlantenLijst>(
      "/api/werkplek/klanten",
      { params: zoek ? { zoek } : undefined },
    );
    return data;
  },

  async pagina(klantId: string): Promise<WerkplekKlantPagina> {
    const { data } = await apiClient.get<WerkplekKlantPagina>(
      `/api/werkplek/klanten/${klantId}`,
    );
    return data;
  },

  async orders(klantId: string): Promise<WerkplekKlantOrder[]> {
    const { data } = await apiClient.get<WerkplekKlantOrder[]>(
      `/api/werkplek/klanten/${klantId}/orders`,
    );
    return data;
  },

  async gesprekken(klantId: string): Promise<WerkplekKlantGesprek[]> {
    const { data } = await apiClient.get<WerkplekKlantGesprek[]>(
      `/api/werkplek/klanten/${klantId}/gesprekken`,
    );
    return data;
  },

  async notities(klantId: string): Promise<WerkplekKlantNotitie[]> {
    const { data } = await apiClient.get<WerkplekKlantNotitie[]>(
      `/api/werkplek/klanten/${klantId}/notities`,
    );
    return data;
  },

  /** Producten voor het rekenblok, eerder gekocht voorop (sessie 46, B9). */
  async voedingProducten(
    klantId: string,
    zoek?: string,
  ): Promise<WerkplekVoedingProduct[]> {
    const { data } = await apiClient.get<WerkplekVoedingProduct[]>(
      `/api/werkplek/klanten/${klantId}/voeding/producten`,
      { params: zoek ? { zoek } : undefined },
    );
    return data;
  },

  async berekenVoeding(
    klantId: string,
    invoer: {
      huisdier_id: string;
      product_item_id: string;
      gewicht_kg?: number | null;
      activiteitsniveau?: string | null;
      /** Sessie 47 (6b): voor een pup zonder dit veld in het profiel. */
      verwacht_volwassen_gewicht_kg?: number | null;
    },
  ): Promise<WerkplekVoedingUitkomst> {
    const { data } = await apiClient.post<WerkplekVoedingUitkomst>(
      `/api/werkplek/klanten/${klantId}/voeding/bereken`,
      invoer,
    );
    return data;
  },

  async toegang(): Promise<WerkplekToegangLijst> {
    const { data } = await apiClient.get<WerkplekToegangLijst>(
      "/api/werkplek/toegang",
    );
    return data;
  },
};


// ── Bouwer 1: kaarten, cijfers, zaak-detail en de panelen ────────────

export interface KaartChip {
  /** bedrag | live | klacht | wekker | terugbellen | formulier | systeem */
  soort: string;
  tekst: string | null;
}

export interface KaartPostbus {
  /** Het adres tot de apenstaart, zoals de mock: "klantenservice@". */
  naam: string;
  kleur: string;
}

export interface Kaart {
  zaak_id: string;
  /**
   * mail | whatsapp | chat | telefoon | systeem, in volgorde van binnenkomst.
   * Leeg bij een losse taak: die draagt alleen de chip Taak (sessie 47).
   */
  kanalen: string[];
  chips: KaartChip[];
  naam: string;
  moment: string;
  onderwerp: string | null;
  waarom: string;
  postbus: KaartPostbus | null;
  /** nieuw | open | afgerond */
  status: string;
  afgerond: boolean;
  klant_id: string | null;
  /** Sessie 48 (E1): wat de open taak is, als tweede regel. */
  taak_tekst?: string | null;
  /**
   * Sessie 52b: het kanaal van het laatste klantbericht. `kanalen` begint
   * hiermee; `bijkanalen` is de rest, voor het bijschrift.
   */
  hoofdkanaal?: string | null;
  bijkanalen?: string[];
  /** Sessie 52b: een belofte of beslis-kaart wacht op JP. */
  beslis?: boolean;
  /** Sessie 52b: wat JP moet doen, in zijn woorden. */
  opvolgstap?: string | null;
  /** Fix-ronde 52b: het voorstel heeft nog een open plek ("vul nog in"). */
  vul_nog_in?: boolean;
}

const KORTING_PLEKKEN = ["KORTINGSCODE", "KORTINGS_CODE", "CODE", "KORTINGSCODE_HIER"];
const RETOUR_PLEKKEN = ["RETOURLABEL", "RETOUR_LABEL", "RETOURLINK", "RETOUR_LINK"];

/**
 * Fix-ronde 52b: staat er nog een open plek in een bericht
 * (`[KORTINGSCODE]`, `[RETOURLABEL]`, `<code>`)? Dan de melding voor JP,
 * anders `null`. Zelfde regel als `plaatshouder_melding` in
 * `app/services/werkplek_verzenden.py`; de server weigert zo'n bericht ook.
 */
export function plaatshouderMelding(tekst: string | null | undefined): string | null {
  if (!tekst) return null;
  const patroon =
    /\[([A-Za-z][A-Za-z_ ]{3,})\]|<\s*(code|kortingscode|kortings_code|retourlabel|retour_label|link|naam)\s*>/gi;
  for (const treffer of tekst.matchAll(patroon)) {
    const blok = treffer[1];
    const tag = (treffer[2] ?? "").toLowerCase();
    const naam = (blok ?? tag).trim().toUpperCase().replace(/ /g, "_");
    // Recensie 52b: een bekende plek telt in elke schrijfwijze ("[Kortingscode]").
    const bekend = KORTING_PLEKKEN.includes(naam) || RETOUR_PLEKKEN.includes(naam);
    if (blok !== undefined && blok !== blok.toUpperCase() && !bekend) continue;
    // Alleen een echte tag met inhoud erna is geen open plek.
    const erna = tekst.slice((treffer.index ?? 0) + treffer[0].length).toLowerCase();
    const sluit = erna.indexOf("</code>");
    const volgende = erna.indexOf("<code");
    if (tag === "code" && sluit !== -1 && (volgende === -1 || sluit < volgende)) continue;
    if (KORTING_PLEKKEN.includes(naam)) return "Vul eerst de kortingscode in";
    if (RETOUR_PLEKKEN.includes(naam)) return "Vul eerst het retourlabel in";
    return `Vul eerst ${treffer[0]} in, dat staat nog open in het bericht`;
  }
  return null;
}

export interface KaartenBlok {
  kaarten: Kaart[];
  tellers: Record<string, number>;
  volgende_vanaf: string | null;
}

export interface WerkplekCijfers {
  door_jou: number;
  door_sanne_en_nova: number;
  langste_wachttijd_minuten: number;
  langste_wachttijd_zaak_id: string | null;
  /** Open taken uit het actiecentrum (sessie 47, 3a). */
  taken_voor_jou?: number;
}

export interface Klantblok {
  id: string | null;
  naam: string;
  klant_sinds: string | null;
  adres: string | null;
  telefoon: string | null;
  email: string | null;
  aantal_orders: number;
  laatste_order_op: string | null;
  lifetime_value_cents: number;
  aantal_retouren: number;
  open_zaken: number;
  laatst_gesynct_op: string | null;
  sync_bron: string | null;
}

export interface ZaakDier {
  naam: string;
  /** "hond", "kat" — het scherm zet er een hoofdletter voor. */
  diersoort: string | null;
  ras: string | null;
  leeftijd_maanden: number | null;
  gewicht_kg: number | null;
  onbekend: string[];
}

export interface ZaakOrderRegel {
  omschrijving: string;
  aantal: number;
}

export interface ZaakOrder {
  nummer: string;
  portaal_url: string | null;
  lightspeed_url: string | null;
  besteld_op: string | null;
  regels: ZaakOrderRegel[];
  regels_leeg_reden: string | null;
  bedrag_cents: number;
  betaalmethode: string | null;
  betaald: boolean;
  /** Sessie 46 (A9): "gekoppeld", "uit_bericht" of "vermoedelijk". */
  bron?: string;
  /** "Vermoedelijk deze order" of "Ordernummer uit het bericht". */
  bron_label?: string | null;
  /** Sessie 47 (4b): andere open orders van dezelfde klant. */
  ook_open?: { nummer: string; stand: string; besteld_op: string | null }[];
}

export interface ZaakVerzending {
  partij: string | null;
  moment: string | null;
  gewicht_kg: number | null;
  dozen: number | null;
  leeg_reden: string | null;
}

export interface ZaakZending {
  vervoerder: string | null;
  code: string | null;
  track_url: string | null;
  status: string | null;
  bezorgd_op: string | null;
  plek: string | null;
  handtekening: boolean | null;
}

export interface ZaakRegel {
  tekst: string;
  beslist_door: string | null;
}

export interface ZaakCase {
  ticket_nummer: string | null;
  status: string | null;
}

export interface ZaakWekkerRegel {
  reden: string;
  afloopt_op: string;
  status: string;
}

export interface ZaakRetour {
  reden: string | null;
  label: string | null;
  /** Sessie 169 (C3): label_url vervalt; alleen of er een label is. */
  label_beschikbaar: boolean;
  /** Sessie 169: om het label als blob op te halen. */
  retour_id?: string | null;
  ontvangen_op: string | null;
  /** Sessie 166: binnen volgens de tijdlijn, ook als het moment onbekend is. */
  binnen?: boolean;
}

export interface ZaakBijlage {
  id: string;
  naam: string;
  soort: string | null;
  thumbnail_url: string | null;
  volledig_url: string | null;
}

/** Eén beurt in een chat- of WhatsApp-gesprek (JP, punt 15). */
export interface ZaakBallon {
  id: string;
  /** "klant" (links) of "wij" (rechts). */
  kant: "klant" | "wij";
  wie: string;
  tekst: string;
  moment: string;
  /** Sessie 52b: WhatsApp, alleen bij ons bericht: "verzonden",
   *  "bezorgd", "gelezen" of "mislukt". */
  verzendstatus?: string | null;
  /** Sessie 52b: tekst, afbeelding, spraak, ... (WhatsApp). */
  bericht_soort?: string | null;
  /** Sessie 52b: het uitgeschreven spraakbericht. */
  transcript?: string | null;
  /** Sessie 52b: de foto's bij deze beurt. */
  fotos?: ZaakBallonFoto[];
}

/** Sessie 52b: een foto bij een beurt, met wat Nova erop zag. De `url`
 *  vraagt een token: laad de foto als blob via `adminChatApi.bijlage`. */
export interface ZaakBallonFoto {
  bericht_id: string;
  naam: string;
  soort?: string | null;
  url: string;
  /** De korte samenvatting van de eerste blik. */
  blik?: string | null;
  /** Fix-ronde 52b: "chat" of "mail". Bij "mail" is `bericht_id` de id
   *  van de mailbijlage; laad hem via `emailsApi.bijlageInhoud`. */
  bron?: "chat" | "mail" | string;
  email_id?: string | null;
}

/** Sessie 52b: één controle uit het schadedossier, in gewone taal. */
export interface SchadeControle {
  naam: string;
  /** "ok", "niet_ok" of "onbekend". */
  uitkomst: string;
  /** "in orde", "niet in orde" of "onbekend". */
  uitkomst_tekst: string;
  reden?: string | null;
}

/** Sessie 52b: het schadedossier op de zaak (`SchadeMelding`). */
export interface Schadedossier {
  id: string;
  stand: string;
  stand_tekst: string;
  kanaal: string;
  ordernummer?: string | null;
  fotos: ZaakBallonFoto[];
  /** Wat Nova op de foto's zag, regel voor regel in gewone taal. */
  wat_nova_zag: string[];
  controles: SchadeControle[];
  zwaarte?: string | null;
  aangeboden: string[];
  keuze_klant?: string | null;
  reden_jp?: string | null;
  moment?: string | null;
}

export interface ZaakBericht {
  samenvatting: string | null;
  /** De beurten van een gesprek; leeg bij mail. */
  ballonnen?: ZaakBallon[];
  bijlagen: ZaakBijlage[];
  ai_bijlagen_regel: string | null;
  origineel_url: string;
  eerdere_gesprekken: number;
}

export interface ZaakDetail {
  /** Sessie 52b: het nieuwste schadedossier op de zaak, anders leeg. */
  schadedossier?: Schadedossier | null;
  zaak: Zaak;
  klant: Klantblok | null;
  dieren: ZaakDier[];
  order: ZaakOrder | null;
  verzending: ZaakVerzending | null;
  zending: ZaakZending | null;
  regels: ZaakRegel[];
  /** Sessie 47 (4a): waarom er geen kaderregel geldt. */
  regels_leeg_reden?: string | null;
  /** Sessie 47 (4a): de zendingstatus wordt op de achtergrond opgehaald. */
  zending_ophalen?: boolean;
  case: ZaakCase | null;
  wekker: ZaakWekkerRegel | null;
  retour: ZaakRetour | null;
  bericht: ZaakBericht | null;
  /** Sessie 48 (A3): het telefoongesprek van deze zaak. */
  telefoon?: ZaakTelefoon | null;
}

/** Sessie 48 (A3/A4): een gesprek in woorden, met de belnotitie. */
export interface ZaakTelefoon {
  gesprek_id: string;
  bron: string;
  richting: "inkomend" | "uitgaand" | string;
  moment: string;
  duur_seconden: number | null;
  nummer: string | null;
  opgenomen: boolean | null;
  gemist: boolean;
  uitkomst_tekst: string;
  doorverbonden: boolean;
  doorverbonden_naar: string | null;
  terugbellen: boolean;
  terugbellen_reden: string | null;
  terugbelverzoek: boolean;
  notitie: {
    verslag: string | null;
    beller: string | null;
    nummer: string | null;
    ordernummer: string | null;
    aangenomen_door: string | null;
    moment_tekst: string | null;
  } | null;
  heeft_opname: boolean;
}

export interface Origineel {
  onderwerp: string | null;
  van: string | null;
  moment: string | null;
  tekst: string | null;
  html: string | null;
}

export interface EerderGesprek {
  id: string;
  kanaal: string;
  onderwerp: string | null;
  moment: string;
  samenvatting: string | null;
}

export interface NieuwBerichtUitkomst {
  /** Leeg bij telefoon: een klik op Bel maakt geen zaak (sessie 47, 9d). */
  zaak_id: string | null;
  kanaal: string;
  stem: string;
  naar: string | null;
  reden_uit: string | null;
  klant_id?: string | null;
}

/** Sessie 47 (6a): het dier uit het bericht, om vooraf in te vullen. */
export interface DierVoorstel {
  naam: string | null;
  diersoort: string | null;
  ras: string | null;
  leeftijd_maanden: number | null;
  gewicht_kg: number | null;
  verwacht_volwassen_gewicht_kg: number | null;
  velden: string[] | null;
  bron: string;
}

export const kaartenApi = {
  /** Sessie 47 (7a): de kaart achter een chat of mail uit een pushmelding. */
  async zaakVanBron(
    soort: "chat" | "mail",
    id: string,
  ): Promise<{ zaak_id: string | null }> {
    const { data } = await apiClient.get<{ zaak_id: string | null }>(
      "/api/werkplek/zaak-van-bron",
      { params: { soort, id } },
    );
    return data;
  },

  async dierVoorstel(zaakId: string): Promise<DierVoorstel> {
    const { data } = await apiClient.get<DierVoorstel>(
      `/api/werkplek/zaken/${zaakId}/dier-voorstel`,
    );
    return data;
  },

  async kaarten(invoer: {
    filter?: string;
    vanaf?: string | null;
    limiet?: number;
    zoek?: string | null;
  }): Promise<KaartenBlok> {
    const { data } = await apiClient.get<KaartenBlok>(
      "/api/werkplek/kaarten",
      {
        params: {
          filter: invoer.filter ?? "nieuw",
          vanaf: invoer.vanaf ?? undefined,
          limiet: invoer.limiet ?? 50,
          zoek: invoer.zoek || undefined,
        },
      },
    );
    return data;
  },

  /**
   * Zelf een gesprek beginnen met een klant (JP, punt 12 van 22-09).
   * Zet een lege zaak klaar; er wordt niets verstuurd.
   */
  async nieuwBericht(invoer: {
    /** Leeg mag bij een los adres of nummer (sessie 46, B2). */
    klant_id?: string | null;
    adres?: string | null;
    kanaal: "mail" | "whatsapp" | "telefoon";
    stem: "sanne" | "nova" | "jp";
    onderwerp?: string | null;
    /** Sessie 72: waar de knop stond, voor het meet-event. */
    bron?: "vandaag" | "klantblok" | "klantpagina" | null;
  }): Promise<NieuwBerichtUitkomst> {
    const { data } = await apiClient.post<NieuwBerichtUitkomst>(
      "/api/werkplek/zaken/nieuw",
      {
        klant_id: invoer.klant_id ?? null,
        adres: invoer.adres || null,
        kanaal: invoer.kanaal,
        stem: invoer.stem,
        onderwerp: invoer.onderwerp || null,
        bron: invoer.bron ?? null,
      },
    );
    return data;
  },

  async cijfers(): Promise<WerkplekCijfers> {
    const { data } = await apiClient.get<WerkplekCijfers>(
      "/api/werkplek/cijfers",
    );
    return data;
  },

  async detail(zaakId: string): Promise<ZaakDetail> {
    const { data } = await apiClient.get<ZaakDetail>(
      `/api/werkplek/zaken/${zaakId}/detail`,
    );
    return data;
  },

  async origineel(zaakId: string): Promise<Origineel> {
    const { data } = await apiClient.get<Origineel>(
      `/api/werkplek/zaken/${zaakId}/origineel`,
    );
    return data;
  },

  async eerdereGesprekken(zaakId: string): Promise<EerderGesprek[]> {
    const { data } = await apiClient.get<EerderGesprek[]>(
      `/api/werkplek/zaken/${zaakId}/eerdere-gesprekken`,
    );
    return data;
  },
};


// ── Bouwer 2: reactietegel, uitgaande wachtrij, bellen ───────────────

export interface TegelBijlage {
  id: string;
  naam: string;
  soort: string;
  grootte_bytes: number | null;
}

export interface TegelConcept {
  zaak_id: string;
  /** "mail" | "whatsapp" | "chat" */
  kanaal: string;
  aan: string[];
  cc: string[];
  bcc: string[];
  onderwerp: string | null;
  tekst: string;
  handtekening_html: string;
  handtekening_tekst: string;
  /** "sanne" | "nova" | "jp" */
  stem: string;
  bijlagen: TegelBijlage[];
  /** "sanne" | "wekker" | "leeg" */
  bron: string;
  /** "oranje" | "akkoord" | "groen" (sessie 50) */
  stoplicht: string;
  vertrouwen: number | null;
  /** Waarom er geen voorstel is. Staat letterlijk op het scherm. */
  reden_leeg: string | null;
  /**
   * Sessie 46 (B4): "geen", "bezig", "klaar" of "mislukt"; sessie 47:
   * "afronden" als het verhaal rond is.
   */
  voorstel_stand?: string;
  voorstel_reden?: string | null;
  /** Sessie 47 (2b): afronden zonder antwoord, met reden en label. */
  afronden_reden?: string | null;
  afronden_label?: string | null;
  /** "Afgerond door JP, bedankje": afgerond zonder antwoord. */
  afgerond_waarom?: string | null;
  /**
   * Deze zaak is af: het laatste woord in de draad is een verstuurd
   * antwoord. De tegel toont de tekst dan alleen-lezen.
   */
  afgerond?: boolean;
  /** "Sanne" | "Nova" | "jou" — of null als niemand het vastlegde. */
  verstuurd_door?: string | null;
  /** UTC; het scherm toont Amsterdamse tijd. */
  verstuurd_op?: string | null;
  /**
   * Afgerond, maar zonder mail uit het portaal: elders beantwoord of
   * gearchiveerd. De tegel zegt dat dan met zoveel woorden.
   */
  afgerond_zonder_mail?: boolean;
  /**
   * Sessie 72 (punt 6): het versturen mislukte met deze reden. Dan is
   * `bron` "mislukt" en zijn tekst, aan, cc, bcc en onderwerp die uit de
   * wachtrij, ongewijzigd. Anders null of afwezig.
   */
  verzend_fout?: string | null;
  /**
   * Sessie 55: label en pakbon die de retourmotor bij dit concept klaarzette
   * (`V6BijlageAntwoord`). Het antwoordvak vult ze één keer per concept voor
   * in de mailstand. Afwezig bij een oudere backend.
   */
  concept_bijlagen?: ConceptBijlage[] | null;
}

/** Een klaargezette bijlage bij het concept: dezelfde vorm als een upload
 *  of `POST /zaken/{id}/documenten/{soort}` (V6BijlageAntwoord). */
export interface ConceptBijlage {
  id: string;
  naam: string;
  soort: string;
  grootte_bytes: number;
}

export interface TegelHandtekening {
  stem: string;
  handtekening_html: string;
  handtekening_tekst: string;
  reden_leeg: string | null;
}

export interface TegelNetjes {
  netjes: string;
  origineel: string;
  onderwerp_voorstel: string | null;
}

export interface TegelVersturen {
  kanaal: string;
  aan?: string[];
  cc?: string[];
  bcc?: string[];
  onderwerp?: string | null;
  tekst: string;
  stem?: string;
  bijlage_ids?: string[];
  /** "nu" | "vandaag_17" | "morgen_08" | ISO-moment */
  moment?: string;
  actie_id?: string | null;
  /** Sessie 75: "Akkoord en versturen" vanuit Beslissen; komt de belofte na. */
  uit_beslissen?: boolean;
  sanne_leert?: boolean;
  /** Bewust een tweede bericht op een afgeronde zaak. */
  nieuw_bericht?: boolean;
  /** Sessie 48 (B1): JP bevestigde een tekst die al verstuurd is. */
  bevestig_dubbel?: boolean;
}

export interface TegelVerstuurd {
  wachtrij_id: string;
  verstuurt_op: string;
  /** Het venster sluit precies wanneer het bericht vertrekt. */
  ongedaan_tot: string;
  kanaal: string;
  actie_regel: string | null;
}

export interface WachtrijRij {
  id: string;
  kanaal: string;
  /** "wacht" | "verstuurd" | "ingetrokken" | "mislukt" */
  status: string;
  onderwerp: string | null;
  verstuurt_op: string;
  verstuurd_op: string | null;
  ongedaan_op: string | null;
  mag_intrekken: boolean;
  foutmelding: string | null;
  actie_id: string | null;
}

export interface TegelNotitie {
  id: string;
  moment: string;
  soort: string;
  tekst: string;
  door_soort: string;
  details: Record<string, unknown> | null;
}

export interface BelGesprekRegel {
  id: string;
  nummer: string | null;
  richting: string;
  moment: string;
  duur_seconden: number | null;
  klant_id: string | null;
  klant_naam: string | null;
  terugbellen: boolean;
}

export interface Belvenster {
  voorgesteld_nummer: string | null;
  klant_id: string | null;
  klant_naam: string | null;
  recent: BelGesprekRegel[];
  terugbellen: BelGesprekRegel[];
  kan_bellen: boolean;
  reden_uit: string | null;
}

export interface BelGestart {
  gestart: boolean;
  nummer: string;
  klant_id: string | null;
  klant_naam: string | null;
  gestart_op: string;
  /** Rinkel biedt deze twee niet via de API (JP, 21-09). */
  kan_dempen: boolean;
  kan_ophangen: boolean;
}

export interface AfrondReden {
  sleutel: string;
  label: string;
}

export interface AfrondUitkomst {
  zaak_id: string;
  event_id: string | null;
  reden: string;
  waarom: string;
  ongedaan_tot: string;
  mails_gesloten: number;
}

export interface SignaalKaart {
  id: string;
  bron: string;
  soort: string;
  titel: string;
  wat: string | null;
  moment: string;
  open: boolean;
  bron_label: string;
  /** Checkout 5: null = geen bronknop (geen eigen scherm om naartoe te gaan). */
  bron_pad: string | null;
  /** Sessie 48 (E3): "gezien", "hersteld", "referentie". */
  acties?: string[];
  afgehandeld_op?: string | null;
  afgehandeld_reden?: string | null;
  patroon?: string | null;
  staafje?: { dag: string; waarde: number; tekst: string; boven_norm: boolean }[];
  norm?: string | null;
}

/** Sessie 47 (3e): technische signalen, één kaart per signaal. */
export const signalenApi = {
  async lijst(): Promise<SignaalKaart[]> {
    const { data } = await apiClient.get<SignaalKaart[]>("/api/werkplek/signalen");
    return data;
  },
  /** Sessie 48 (E3): afhandelen met een reden, op Signalen zelf. */
  async afhandelen(
    bron: string,
    id: string,
    actie: string,
    reden: string,
  ): Promise<void> {
    await apiClient.post(`/api/werkplek/signalen/${bron}/${id}/afhandelen`, {
      actie,
      reden,
    });
  },
  async heropen(bron: string, id: string): Promise<void> {
    await apiClient.post(`/api/werkplek/signalen/${bron}/${id}/heropen`);
  },
  /**
   * Sessie 61 (M2): klanten van wie het voer binnen `dagen` op is.
   * Droogstand: er gaat geen bericht uit, dit is alleen de lijst.
   */
  async bijnaOp(dagen = 7): Promise<BijnaOpLijst> {
    const { data } = await apiClient.get<BijnaOpLijst>(
      "/api/werkplek/signalen/bijna-op",
      { params: { dagen } },
    );
    return data;
  },
};

// ── Sessie 61: voer-op-datum (M2) ───────────────────────────────────

/** "model" (dagdosering), "historie" (echte herhaalbestellingen), "mix" of "onbekend". */
export type VoerOpDatumBron = "model" | "historie" | "mix" | "onbekend";

export interface BijnaOpRegel {
  klant_id: string;
  klant_naam: string;
  product_naam: string;
  op_datum: string;
  bron: VoerOpDatumBron;
}

export interface BijnaOpLijst {
  aantal: number;
  berekend_op: string | null;
  regels: BijnaOpRegel[];
}

/** Sessie 47 (deel 2): afronden zonder antwoord. Er gaat niets naar de klant. */
export const afrondenApi = {
  async redenen(): Promise<AfrondReden[]> {
    const { data } = await apiClient.get<AfrondReden[]>(
      "/api/werkplek/afronden/redenen",
    );
    return data;
  },
  async rondAf(zaakId: string, reden: string): Promise<AfrondUitkomst> {
    const { data } = await apiClient.post<AfrondUitkomst>(
      `/api/werkplek/zaken/${zaakId}/afronden`,
      { reden },
    );
    return data;
  },
  async ongedaan(zaakId: string, eventId: string): Promise<void> {
    await apiClient.post(`/api/werkplek/zaken/${zaakId}/afronden/ongedaan`, {
      event_id: eventId,
    });
  },
  async tochAntwoorden(
    zaakId: string,
  ): Promise<{ stand: string; door: string | null; reden: string | null }> {
    const { data } = await apiClient.post<{
      stand: string;
      door: string | null;
      reden: string | null;
    }>(`/api/werkplek/zaken/${zaakId}/afronden/toch-antwoorden`);
    return data;
  },
};

export const werkplekTegelApi = {
  /** Sessie 46 (B4): laat Sanne of Nova een voorstel maken; verstuurt niets. */
  async vraagVoorstel(
    zaakId: string,
  ): Promise<{ stand: string; door: string | null; reden: string | null }> {
    const { data } = await apiClient.post<{
      stand: string;
      door: string | null;
      reden: string | null;
    }>(`/api/werkplek/zaken/${zaakId}/voorstel`);
    return data;
  },

  async concept(zaakId: string, stem?: string): Promise<TegelConcept> {
    const { data } = await apiClient.get<TegelConcept>(
      `/api/werkplek/zaken/${zaakId}/concept`,
      { params: stem ? { stem } : undefined },
    );
    return data;
  },

  async handtekening(stem: string): Promise<TegelHandtekening> {
    const { data } = await apiClient.get<TegelHandtekening>(
      `/api/werkplek/handtekening/${stem}`,
    );
    return data;
  },

  /** Enter-flow. Verstuurt nooit; geeft de nette versie plus het origineel. */
  async netjes(
    zaakId: string,
    invoer: { tekst: string; kanaal: string; stem?: string },
  ): Promise<TegelNetjes> {
    const { data } = await apiClient.post<TegelNetjes>(
      `/api/werkplek/zaken/${zaakId}/netjes`,
      invoer,
    );
    return data;
  },

  /** Zet klaar in de wachtrij. Er gaat hier nog niets de deur uit. */
  async versturen(
    zaakId: string,
    invoer: TegelVersturen,
  ): Promise<TegelVerstuurd> {
    const { data } = await apiClient.post<TegelVerstuurd>(
      `/api/werkplek/zaken/${zaakId}/versturen`,
      invoer,
    );
    return data;
  },

  async ongedaan(wachtrijId: string): Promise<WachtrijRij> {
    const { data } = await apiClient.post<WachtrijRij>(
      `/api/werkplek/wachtrij/${wachtrijId}/ongedaan`,
    );
    return data;
  },

  async wachtrij(zaakId: string): Promise<WachtrijRij[]> {
    const { data } = await apiClient.get<WachtrijRij[]>(
      `/api/werkplek/zaken/${zaakId}/wachtrij`,
    );
    return data;
  },

  async notitie(zaakId: string, tekst: string): Promise<TegelNotitie> {
    const { data } = await apiClient.post<TegelNotitie>(
      `/api/werkplek/zaken/${zaakId}/notitie`,
      { tekst },
    );
    return data;
  },

  async belvenster(params: {
    zaakId?: string | null;
    klantId?: string | null;
  }): Promise<Belvenster> {
    const { data } = await apiClient.get<Belvenster>(
      "/api/werkplek/bellen/venster",
      {
        params: {
          zaak_id: params.zaakId ?? undefined,
          klant_id: params.klantId ?? undefined,
        },
      },
    );
    return data;
  },

  async bellen(invoer: {
    nummer: string;
    zaak_id?: string | null;
    klant_id?: string | null;
    /** Sessie 47 (9d): waar de knop stond. */
    bron?: string;
  }): Promise<BelGestart> {
    const { data } = await apiClient.post<BelGestart>(
      "/api/werkplek/bellen",
      invoer,
    );
    return data;
  },
};

// ── Bouwer 4, ronde 2: Gezondheid, Kennis en Beheer ──────────────────

export interface GezondheidKanaal {
  kanaal: string;
  label: string;
  /** Naam van de Okabe-Ito-rol; de waarde staat in werkplek-tokens.css. */
  kleur: string;
}

export interface GezondheidStaaf {
  sleutel: string;
  label: string;
  kort: string;
  per_kanaal: Record<string, number>;
  totaal: number;
  lopend: boolean;
}

export interface GezondheidKerncijfer {
  sleutel: string;
  label: string;
  waarde: string;
  /** Wat dit cijfer meet; staat in het blok (JP, punt 41). */
  uitleg?: string | null;
  verschil: string | null;
  richting: string | null;
}

export interface GezondheidKlantenservice {
  periode: string;
  aantal: number;
  staven: GezondheidStaaf[];
  kanalen: GezondheidKanaal[];
  kerncijfers: GezondheidKerncijfer[];
  kerncijfer_label: string | null;
  /** De normlijn in de grafiek, plus het label ernaast (JP, punt 38). */
  norm?: number | null;
  norm_label?: string | null;
  vorige_label: string | null;
  maximum: number;
  reden_leeg: string | null;
}

export interface GezondheidSchrijver {
  schrijver: string;
  label: string;
  gemiddelde: number | null;
  aantal: number;
}

export interface GezondheidTevredenheid {
  periode_label: string;
  verstuurd: number;
  antwoorden: number;
  aandeel_procent: number | null;
  gemiddelde: number | null;
  per_schrijver: GezondheidSchrijver[];
  onder_drie: number;
  onder_drie_categorieen: string[];
  uitleg: string | null;
  reden_leeg: string | null;
}

export interface GezondheidFeit {
  term: string;
  waarde: string;
  stand: string | null;
  chip: string | null;
  /** Eén regel uitleg onder de waarde; null waar hij niet nodig is. */
  toelichting?: string | null;
}

export interface GezondheidBlok {
  titel: string;
  ondertitel: string | null;
  feiten: GezondheidFeit[];
  reden_leeg: string | null;
}

export interface GezondheidBlokken {
  blokken: GezondheidBlok[];
}

export const werkplekGezondheidApi = {
  async klantenservice(
    periode: string,
    aantal?: number,
  ): Promise<GezondheidKlantenservice> {
    const { data } = await apiClient.get<GezondheidKlantenservice>(
      "/api/werkplek/gezondheid/klantenservice",
      { params: { periode, aantal } },
    );
    return data;
  },

  async tevredenheid(): Promise<GezondheidTevredenheid> {
    const { data } = await apiClient.get<GezondheidTevredenheid>(
      "/api/werkplek/gezondheid/tevredenheid",
    );
    return data;
  },

  async checkout(): Promise<GezondheidBlokken> {
    const { data } = await apiClient.get<GezondheidBlokken>(
      "/api/werkplek/gezondheid/checkout",
    );
    return data;
  },

  async systeem(): Promise<GezondheidBlokken> {
    const { data } = await apiClient.get<GezondheidBlokken>(
      "/api/werkplek/gezondheid/systeem",
    );
    return data;
  },

  /** Sessie 47 (2c): afgerond zonder antwoord per week. */
  async afronden(): Promise<GezondheidBlokken> {
    const { data } = await apiClient.get<GezondheidBlokken>(
      "/api/werkplek/gezondheid/afronden",
    );
    return data;
  },
};

/** Sessie 50: stand per (vraagtype, handeling) uit de motor. Sessie 53:
 *  vier standen, rood erbij (app/schemas/werkplek_stoplicht.py). */
export type KennisStoplichtStand = "rood" | "oranje" | "akkoord" | "groen";

export interface KennisStoplichtRegel {
  vraagtype: string;
  vraagtype_naam: string;
  handeling: string;
  handeling_naam: string;
  stand: KennisStoplichtStand;
  teller_ongewijzigd: number;
  n_doorschuiven: number;
}

export interface KennisHandeling {
  sleutel: string;
  naam: string;
  aan: boolean;
  schrijvend: boolean;
}

export interface KennisStoplicht {
  regels: KennisStoplichtRegel[];
  handelingen: KennisHandeling[];
}

/** Sessie 50: het gedragsdocument, de enige bron voor de prompts. */
export type GedragLaagSoort = "kern" | "kanaal" | "vraagtype" | "leerregel";

export interface GedragRegel {
  id: string;
  onderwerp: string;
  tekst: string;
  /** JJJJ-MM-DD */
  datum: string;
  bron: string;
  kanaal: string | null;
  vraagtype: string | null;
}

export interface GedragLaag {
  laag: GedragLaagSoort;
  titel: string;
  regels: GedragRegel[];
}

export interface Gedragsdocument {
  lagen: GedragLaag[];
}

export interface KennisLeerWeek {
  week: string;
  totaal: number;
  ongewijzigd: number;
  percentage: number;
}

export interface KennisLeren {
  totaal: number;
  ongewijzigd: number;
  gewijzigd: number;
  percentage_ongewijzigd: number;
  weken: KennisLeerWeek[];
  per_categorie: { categorie: string; aantal: number }[];
  reden_leeg: string | null;
}

export interface KennisBron {
  id: string;
  titel: string;
  soort: string;
  vindplaats: string | null;
  status: string;
  actief: boolean;
  aantal_chunks: number;
  zonder_embedding: boolean;
}

export interface KennisBronnen {
  totaal: number;
  verlopen: number;
  binnenkort: number;
  zonder_embedding: number;
  bronnen: KennisBron[];
  reden_leeg: string | null;
}

export interface KennisItem {
  id: string;
  categorie: string;
  vraag: string;
  antwoord: string;
  actief: boolean;
  aantal_keer_gebruikt: number;
  laatst_gebruikt_op: string | null;
}

export interface Kennisbank {
  totaal: number;
  categorieen: string[];
  items: KennisItem[];
  reden_leeg: string | null;
}

export interface KennisRegel {
  id: string;
  titel: string;
  voorstel: string;
  onderbouwing: string | null;
  soort: string;
  label: string;
  aangemaakt_op: string;
  mag_terugdraaien: boolean;
  reden_uit: string | null;
  /** "3 keer gezien, opnieuw gezien op 16-09" (sessie 46, B8). */
  gezien?: string | null;
}

export interface KennisRegels {
  regels: KennisRegel[];
  actief: number;
  open_voorstellen: number;
  reden_leeg: string | null;
}

export const werkplekKennisApi = {
  async stoplicht(): Promise<KennisStoplicht> {
    const { data } = await apiClient.get<KennisStoplicht>(
      "/api/werkplek/kennis/stoplicht",
    );
    return data;
  },

  async gedragsdocument(): Promise<Gedragsdocument> {
    const { data } = await apiClient.get<Gedragsdocument>(
      "/api/werkplek/kennis/gedragsdocument",
    );
    return data;
  },

  async leren(): Promise<KennisLeren> {
    const { data } = await apiClient.get<KennisLeren>(
      "/api/werkplek/kennis/leren",
    );
    return data;
  },

  async bronnen(): Promise<KennisBronnen> {
    const { data } = await apiClient.get<KennisBronnen>(
      "/api/werkplek/kennis/bronnen",
    );
    return data;
  },

  async kennisbank(zoek?: string): Promise<Kennisbank> {
    const { data } = await apiClient.get<Kennisbank>(
      "/api/werkplek/kennis/kennisbank",
      { params: { zoek: zoek || undefined } },
    );
    return data;
  },

  async regels(): Promise<KennisRegels> {
    const { data } = await apiClient.get<KennisRegels>(
      "/api/werkplek/kennis/regels",
    );
    return data;
  },

  /** Zet een actieve regel terug. Hij blijft staan, als teruggedraaid. */
  async draaiRegelTerug(regelId: string): Promise<KennisRegel> {
    const { data } = await apiClient.post<KennisRegel>(
      `/api/werkplek/kennis/regels/${regelId}/terugdraaien`,
    );
    return data;
  },

  /** Sessie 61: de waarheidskaarten, alle drie de standen. */
  async waarheidskaarten(): Promise<Waarheidskaart[]> {
    const { data } = await apiClient.get<Waarheidskaart[]>(
      "/api/werkplek/kennis/kaarten",
    );
    return data;
  },

  /**
   * Sessie 61: een kaart bewerken, goedkeuren of uitzetten. Schrijft
   * direct weg; alleen een beheerder mag dit (anders 403 met reden).
   */
  async wijzigWaarheidskaart(
    kaartId: string,
    wijziging: WaarheidskaartWijziging,
  ): Promise<Waarheidskaart> {
    const { data } = await apiClient.patch<Waarheidskaart>(
      `/api/werkplek/kennis/kaarten/${kaartId}`,
      wijziging,
    );
    return data;
  },
};

// ── Sessie 61: waarheidskaarten (M1) ────────────────────────────────

/** Afgeleid uit de goedkeurstroom van de kennisbank. */
export type WaarheidskaartStatus = "concept" | "goedgekeurd" | "uit";

/** Wat Nova en Sanne met de kaart mogen doen. */
export type WaarheidskaartMag = "uitleggen" | "adviseren" | "waarschuwen";

export interface Waarheidskaart {
  id: string;
  titel: string;
  klanttekst: string;
  /** Intern: de feiten en bronnen achter de kaart. */
  onderbouwing: string | null;
  vraagtype: string | null;
  onderwerp: string | null;
  mag: WaarheidskaartMag | null;
  status: WaarheidskaartStatus;
  bijgewerkt_op: string | null;
  aangemaakt_op: string;
}

export interface WaarheidskaartWijziging {
  titel?: string;
  klanttekst?: string;
  onderbouwing?: string;
  vraagtype?: string;
  mag?: WaarheidskaartMag;
  status?: WaarheidskaartStatus;
}

export interface BeheerKoppeling {
  sleutel: string;
  naam: string;
  waarvoor: string;
  /** "aan" of "wacht_op_sleutel". */
  stand: string;
  /** Namen van de instellingen die nog leeg zijn; nooit een waarde. */
  ontbrekende_velden: string[];
  toelichting: string | null;
  /** Sessie 46 (A12): laatste geslaagde actie, en wat die meet. */
  laatst_gezien?: string | null;
  laatst_gezien_wat?: string | null;
  /** Waarom de stand "controleer" is, in woorden. */
  controleer_reden?: string | null;
}

export interface BeheerPostbus {
  id: string;
  naam: string;
  email_adres: string;
  kleur_hex: string;
  is_default: boolean;
  is_actief: boolean;
  heeft_koppeling: boolean;
}

export interface BeheerHandtekening {
  id: string;
  naam: string;
  functie: string;
  email: string;
  is_ai_persona: boolean;
  actief: boolean;
}

export interface BeheerRegel {
  sleutel: string;
  label: string;
  uitleg: string;
  waarde: boolean | null;
  bestaat: boolean;
  reden_uit: string | null;
}

export interface BeheerStand {
  koppelingen: BeheerKoppeling[];
  postbussen: BeheerPostbus[];
  handtekeningen: BeheerHandtekening[];
  regels: BeheerRegel[];
  koppelingen_aan: number;
  postbussen_actief: number;
}

export const werkplekBeheerApi = {
  async stand(): Promise<BeheerStand> {
    const { data } = await apiClient.get<BeheerStand>(
      "/api/werkplek/beheer/stand",
    );
    return data;
  },
};

// ── Retouren in de werkplek-vorm (bouwer 3, plak 2.5) ────────────────

export interface RetourKaart {
  retour_id: string;
  retournummer: string;
  status: string;
  status_label: string;
  naam: string;
  moment: string;
  wat: string;
  waarom: string;
  bedrag_cents: number;
  klant_id: string | null;
  ordernummer: string | null;
  afgerond: boolean;
}

export interface RetourenBlok {
  kaarten: RetourKaart[];
  tellers: Record<string, number>;
  totaal_in_filter: number;
  /** Sessie 171: het filter dat echt is toegepast. Met een zoekterm altijd
   *  "alles" (zoeken gaat over het statusfilter en het venster heen). */
  filter_toegepast?: string;
}

/** Sessie 171: de ene labelstand van het dossier (reisbalk en Stand). */
export type RetourLabelStand = "geen" | "gemaakt" | "onderweg" | "mislukt" | "verlopen";

export interface RetourCijfers {
  te_beoordelen: number;
  deze_maand: number;
  gecrediteerd_cents: number;
  maand: string;
}

export interface Geldvoorstel {
  sleutel: string;
  soort: string;
  label: string;
  bedrag_cents: number;
  bevestigtekst: string;
  uitleg: string;
}

export interface RetourArtikelRegel {
  productnaam: string;
  aantal: number;
  prijs_cents: number;
  reden: string | null;
  toelichting: string | null;
  verpakking_geopend: boolean | null;
  beslisboom_uitkomst: string;
}

export interface RetourDossier {
  retour_id: string;
  retournummer: string;
  status: string;
  status_label: string;
  kostenoordeel: string;
  kosten_uitleg: string;
  gewenste_oplossing: string | null;
  klant_id: string | null;
  klant_naam: string | null;
  zaak_id: string | null;
  ordernummer: string | null;
  lightspeed_order_url: string | null;
  besteld_op: string | null;
  order_bedrag_cents: number | null;
  reden: string | null;
  /** De vaste redenen van het retourformulier, leesbaar (JP, punt 32). */
  redenen?: string[];
  opmerking_klant: string | null;
  artikelen: RetourArtikelRegel[];
  label_code: string | null;
  /** Sessie 169 (C3): label_url vervalt. Er staat een label klaar. */
  label_beschikbaar: boolean;
  /** Sessie 169 (C3): de pakbon kan worden opgehaald. */
  pakbon_beschikbaar: boolean;
  /** Sessie 169 (C3): JP zette "verpakking geopend" bewust opzij. */
  geopend_overschreven: boolean;
  label_vervoerder: string | null;
  label_aangemaakt_op: string | null;
  /** Sessie 48 (D1): één bron voor de labelstand (ook zonder trackingcode). */
  label_gemaakt?: boolean;
  label_zin?: string;
  /** Sessie 171: spreekt status_label nooit tegen; lees alleen dit veld. */
  label_stand?: RetourLabelStand;
  ontvangen_op: string | null;
  picqer_status: string | null;
  aangemeld_op: string | null;
  boven_drempel: boolean;
  retourkosten_cents: number;
  artikelen_cents: number;
  verzendkosten_cents: number;
  aftrek_cents: number;
  totaal_cents: number;
  compleet: boolean;
  kortingscode_status: string | null;
  kortingscode_stand: string | null;
  kortingscode_code: string | null;
  kortingscode_bedrag_cents: number | null;
  ticket_status: string | null;
  tap_ticket_id: string | null;
  geldvoorstellen: Geldvoorstel[];
  /** De vaste zin uit app/services/terugbetaling_tekst. Nooit zelf
   *  herschrijven: het portaal boekt geen geld terug, de administratie
   *  doet dat met de hand. */
  terugbetaling_zin: string;
  mail_momenten: Record<string, string | null>;
  /** Sessie 55: per artikel wat de klant aanmeldde en wat Cenfil in Picqer
   *  ontving (Picqer is leidend). Afwezig bij een oudere backend. */
  aantallen_aangemeld?: Record<string, number> | null;
  aantallen_ontvangen?: Record<string, number> | null;
  /** Sessie 55: afgerond met een droge geldketen; er is geen geld
   *  teruggeboekt. Het scherm zegt dan nooit "Afgehandeld". */
  droog?: boolean | null;
  /** Sessie 163: de retourtijdlijn (zeven vaste stappen plus eventueel
   *  Geannuleerd of Afgewezen), kant en klaar uit de backend. Afwezig bij
   *  een oudere backend. */
  tijdlijn?: RetourTijdlijnStap[];
  /** Sessie 166: binnen bij Cenfil en verwerkt, uit dezelfde bron als de
   *  tijdlijn. `ontvangen_op` is dan het moment van binnen (als bekend).
   *  Afwezig bij een oudere backend: dan telt `ontvangen_op` alleen. */
  binnen?: boolean;
  verwerkt_op?: string | null;
}

/** Eén stap van de retourtijdlijn (app/services/retour_tijdlijn.py). */
export interface RetourTijdlijnStap {
  sleutel: string;
  label: string;
  gedaan: boolean;
  /** ISO-moment; leeg als de stap gezet is maar het moment niet bewaard. */
  moment: string | null;
  bron: string | null;
}

export interface ZaakVanRetour {
  zaak_id: string;
  retour_id: string;
  nieuw: boolean;
}

/** Sessie 47 (5b): Retour afronden, klaargezet; er is nog niets gebeurd. */
export interface RetourAfrondenUitkomst {
  actie_id: string;
  zaak_id: string;
  bevestigtekst: string;
  onderwerp: string;
  tekst: string;
  code: string | null;
  bedrag_cents: number | null;
  aan: string[];
  /** Sessie 55: aangemeld naast ontvangen (Picqer), per artikel. */
  aantallen_aangemeld?: Record<string, number> | null;
  aantallen_ontvangen?: Record<string, number> | null;
  /** Sessie 55: de geldketen is droog; er gaat geen geld terug. */
  droog?: boolean | null;
}

export const werkplekRetourenApi = {
  async afronden(
    retourId: string,
    invoer: { oplossing: string; bedrag_cents?: number | null; reden?: string | null },
  ): Promise<RetourAfrondenUitkomst> {
    const { data } = await apiClient.post<RetourAfrondenUitkomst>(
      `/api/werkplek/retouren/${retourId}/afronden`,
      invoer,
    );
    return data;
  },

  async lijst(params: {
    filter: string;
    zoek?: string;
    limiet?: number;
  }): Promise<RetourenBlok> {
    const { data } = await apiClient.get<RetourenBlok>(
      "/api/werkplek/retouren",
      {
        params: {
          filter: params.filter,
          zoek: params.zoek || undefined,
          limiet: params.limiet ?? undefined,
        },
      },
    );
    return data;
  },

  async cijfers(): Promise<RetourCijfers> {
    const { data } = await apiClient.get<RetourCijfers>(
      "/api/werkplek/retouren/cijfers",
    );
    return data;
  },

  async dossier(retourId: string): Promise<RetourDossier> {
    const { data } = await apiClient.get<RetourDossier>(
      `/api/werkplek/retouren/${retourId}`,
    );
    return data;
  },

  async zaak(retourId: string): Promise<ZaakVanRetour> {
    const { data } = await apiClient.post<ZaakVanRetour>(
      `/api/werkplek/retouren/${retourId}/zaak`,
    );
    return data;
  },
};

// ── Reviews in de werkplek-vorm (bouwer 3, plak 2.6) ─────────────────

export interface ReviewKaart {
  review_id: string;
  response_id: string | null;
  bron: string;
  bron_label: string;
  score: number | null;
  score_max: number;
  score_label: string;
  negatief: boolean;
  naam: string;
  moment: string | null;
  tekst: string;
  waarom: string;
  status: string;
  afgerond: boolean;
  klant_id: string | null;
  ordernummer: string | null;
}

export interface ReviewsBlok {
  kaarten: ReviewKaart[];
  tellers: Record<string, number>;
  totaal_in_filter: number;
}

export interface Platformcijfer {
  bron: string;
  label: string;
  gemiddelde: number | null;
  schaal: number;
  aantal_maand: number;
  /** "platform" | "eigen_telling" | "onbekend" — het scherm zegt het
   *  erbij; een cijfer zonder herkomst kan niemand narekenen. */
  herkomst: string;
}

export interface ReviewCijfers {
  wacht_op_reactie: number;
  platforms: Platformcijfer[];
  maand: string;
}

export interface Bezwaar {
  regel: string;
  melding: string;
}

export interface ReviewDossier {
  review_id: string;
  response_id: string | null;
  bron: string;
  bron_label: string;
  score: number | null;
  score_max: number;
  score_label: string;
  negatief: boolean;
  naam: string;
  reviewer_email: string | null;
  ordernummer: string | null;
  geplaatst_op: string | null;
  tekst: string;
  taal: string | null;
  klant_id: string | null;
  klant_naam: string | null;
  zaak_id: string | null;
  concept: string | null;
  concept_bron: string;
  status: string;
  status_uitleg: string;
  posted_at: string | null;
  foutbericht: string | null;
  onderwerpen: string[];
  actie_nodig: boolean;
  vraag_in_review: boolean;
  bezwaren: Bezwaar[];
  mag_plaatsen: boolean;
  reden_niet_plaatsen: string | null;
}

export interface ConceptToets {
  mag_plaatsen: boolean;
  reden_niet_plaatsen: string | null;
  bezwaren: Bezwaar[];
}

export interface ReactieGeplaatst {
  geplaatst: boolean;
  status: string;
  posted_at: string | null;
  foutbericht: string | null;
  bezwaren: Bezwaar[];
}

export interface ZaakVanReview {
  zaak_id: string;
  review_id: string;
  nieuw: boolean;
}

export const werkplekReviewsApi = {
  async lijst(params: {
    filter: string;
    zoek?: string;
    limiet?: number;
  }): Promise<ReviewsBlok> {
    const { data } = await apiClient.get<ReviewsBlok>(
      "/api/werkplek/reviews",
      {
        params: {
          filter: params.filter,
          zoek: params.zoek || undefined,
          limiet: params.limiet ?? undefined,
        },
      },
    );
    return data;
  },

  async cijfers(): Promise<ReviewCijfers> {
    const { data } = await apiClient.get<ReviewCijfers>(
      "/api/werkplek/reviews/cijfers",
    );
    return data;
  },

  async dossier(reviewId: string): Promise<ReviewDossier> {
    const { data } = await apiClient.get<ReviewDossier>(
      `/api/werkplek/reviews/${reviewId}`,
    );
    return data;
  },

  /** Wat zegt de stijlgrendel over deze tekst? Leest alleen. */
  async toets(reviewId: string, tekst: string): Promise<ConceptToets> {
    const { data } = await apiClient.post<ConceptToets>(
      `/api/werkplek/reviews/${reviewId}/toets`,
      { tekst },
    );
    return data;
  },

  /**
   * Plaats de publieke reactie. Onomkeerbaar; `bevestigd` moet expliciet
   * mee, anders weigert de backend.
   */
  async plaats(
    reviewId: string,
    invoer: { tekst: string; bevestigd: boolean },
  ): Promise<ReactieGeplaatst> {
    const { data } = await apiClient.post<ReactieGeplaatst>(
      `/api/werkplek/reviews/${reviewId}/plaats`,
      invoer,
    );
    return data;
  },

  async zaak(reviewId: string): Promise<ZaakVanReview> {
    const { data } = await apiClient.post<ZaakVanReview>(
      `/api/werkplek/reviews/${reviewId}/zaak`,
    );
    return data;
  },
};

// ── Afwezigheidsbericht (bouwer 2, plak 2.3) ─────────────────────────

export interface AfwezigheidStand {
  actief: boolean;
  droog: boolean;
  nu_afwezig: boolean;
  /** "weekend" | "avond" | "verlof", of null als er gewerkt wordt. */
  reden: string | null;
  venster_start: string | null;
  venster_einde: string | null;
  reactie_op: string | null;
  avond_van_uur: number;
  avond_tot_uur: number;
  verlofdagen: string[];
  reactie_werkuren: number;
  week_vanaf: string;
  week_tot: string;
  week_verstuurd: number;
  week_droog: number;
  blokkade: string | null;
}

export interface AfwezigheidRonde {
  overgeslagen: string | null;
  droog: boolean;
  reden: string | null;
  venster_start: string | null;
  venster_einde: string | null;
  bekeken: number;
  klaargezet: number;
  droog_gelogd: number;
  redenen: Record<string, number>;
  blokkade: string | null;
}

export const werkplekAfwezigheidApi = {
  async stand(): Promise<AfwezigheidStand> {
    const { data } = await apiClient.get<AfwezigheidStand>(
      "/api/werkplek/afwezigheid",
    );
    return data;
  },

  /** Kijken wat er zou gebeuren. Verstuurt nooit, schrijft niets weg. */
  async proefronde(): Promise<AfwezigheidRonde> {
    const { data } = await apiClient.post<AfwezigheidRonde>(
      "/api/werkplek/afwezigheid/proefronde",
    );
    return data;
  },
};

// ── Ronde 2, bouwer 1: onbekende afzender en samenvoegen ─────────────

export interface KoppelVoorstel {
  voorstel_id: string;
  klant_id: string;
  naam: string | null;
  email: string | null;
  woonplaats: string | null;
  match_type: string;
  match_details: Record<string, string>;
  laatste_order_op: string | null;
}

export interface KoppelBanner {
  nodig: boolean;
  afzender: string | null;
  voorstellen: KoppelVoorstel[];
  mag_koppelen: boolean;
  reden_uitgeschakeld: string | null;
  /** Klantenlijst in de Lightspeed-backoffice; null zonder klantsjabloon. */
  nieuwe_klant_url: string | null;
}

export const werkplekKoppelingApi = {
  async banner(zaakId: string): Promise<KoppelBanner> {
    const { data } = await apiClient.get<KoppelBanner>(
      `/api/werkplek/zaken/${zaakId}/koppeling`,
    );
    return data;
  },

  async koppel(
    zaakId: string,
    klantId: string,
    voorstelId?: string | null,
  ): Promise<KoppelBanner> {
    const { data } = await apiClient.post<KoppelBanner>(
      `/api/werkplek/zaken/${zaakId}/koppeling/${klantId}`,
      null,
      { params: { voorstel_id: voorstelId ?? undefined } },
    );
    return data;
  },

  async negeer(zaakId: string, voorstelId: string): Promise<KoppelBanner> {
    const { data } = await apiClient.post<KoppelBanner>(
      `/api/werkplek/zaken/${zaakId}/koppeling/negeer/${voorstelId}`,
    );
    return data;
  },

  /** "Niet deze klant" bij een al gekoppelde zaak (sessie 70, punt 9). */
  async maakKlantLos(zaakId: string): Promise<KoppelBanner> {
    const { data } = await apiClient.delete<KoppelBanner>(
      `/api/werkplek/zaken/${zaakId}/koppeling`,
    );
    return data;
  },

  /** Haal een zaak weer los uit de zaak waarin hij is opgegaan. */
  async maakSamenvoegingLos(
    zaakId: string,
  ): Promise<{ zaak_id: string; losgemaakt: string }> {
    const { data } = await apiClient.post<{
      zaak_id: string;
      losgemaakt: string;
    }>(`/api/werkplek/zaken/${zaakId}/samenvoeging/ongedaan`);
    return data;
  },
};
